// src/registered/corporate_details/hr_sync/HrSyncController.js
//
// State for the HR & Workforce page. Three ways in, one store (HR_SYNC.Metric):
//   1. a connected HR system (when a global admin has switched the connector on)
//   2. the company's own HR codes, mapped to the ESG HR metrics (kept per company)
//   3. figures typed in by hand for a reporting period

import { useCallback, useEffect, useMemo, useState } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { HR_METRICS, lastCalendarYear, metricsToReportSections } from "./HrSyncDTO";
import { feedReport } from "../feedReport";
import * as api from "./hrSyncInteractionService";

const g = (o, k) => GoldenSourceHelper.get(o, k);

const toConnection = (c) => ({
  id: g(c, "ConnectionId"),
  toolCode: g(c, "ToolCode") || "",
  toolName: g(c, "ToolName") || g(c, "ToolCode") || "HR system",
  status: g(c, "StatusCode") || "",
  lastSyncAt: g(c, "LastSyncAtUtc") || null,
  lastSyncMessage: g(c, "LastSyncMessage") || "",
  canManage: !!g(c, "CanManage"),
});
const toCode = (c) => ({
  id: g(c, "HrCodeId"),
  kind: g(c, "CodeKind") || "OTHER",
  code: g(c, "Code") || "",
  description: g(c, "Description") || "",
  target: g(c, "TargetMetricCode") || "",
});
const toMetric = (m) => ({
  metricCode: g(m, "MetricCode"),
  value: g(m, "Value"),
  unit: g(m, "Unit") || "",
  source: g(m, "SourceCode") || "CONNECTOR",
  toolName: g(m, "ToolName") || "",
});

export default function HrSyncController(companyId, companyName) {
  const [period, setPeriod] = useState(lastCalendarYear());
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");

  const [available, setAvailable] = useState(false);
  const [connections, setConnections] = useState([]);
  const [provider, setProvider] = useState(null); // global admin view of the connector account
  const [providerForm, setProviderForm] = useState({ apiBaseUrl: "", apiKey: "", isEnabled: true });

  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState({ kind: "CONTRACT_TYPE", code: "", description: "" });
  const [pasteText, setPasteText] = useState("");

  const [metrics, setMetrics] = useState([]);
  const [manual, setManual] = useState({}); // metricCode -> string

  const has = companyId != null && companyId !== "";

  const guard = async (label, work, done) => {
    setBusy(label);
    setError("");
    setNotice("");
    try {
      const r = await work();
      if (done) setNotice(typeof done === "function" ? done(r) : done);
      return r;
    } catch (err) {
      setError(err?.message || "Something went wrong.");
      return null;
    } finally {
      setBusy("");
    }
  };

  // ---- loads --------------------------------------------------------------
  const loadStatus = useCallback(async () => {
    if (!has) return;
    try {
      const s = await api.fetchStatus(companyId);
      setAvailable(!!g(s, "Available"));
      setConnections((g(s, "Connections") || []).map(toConnection));
    } catch (err) {
      setError(err?.message || "Could not load the HR connection.");
    }
  }, [companyId, has]);

  const loadProvider = useCallback(async () => {
    try {
      const rows = await api.fetchProviderStatus();
      const p = rows[0];
      if (!p) return;
      const row = {
        canAdminister: !!g(p, "CanAdminister"),
        isEnabled: !!g(p, "IsEnabled"),
        hasKey: !!g(p, "HasKey"),
        apiBaseUrl: g(p, "ApiBaseUrl") || "",
        displayName: g(p, "DisplayName") || "HR connector",
      };
      setProvider(row);
      setProviderForm((f) => ({ ...f, apiBaseUrl: row.apiBaseUrl, isEnabled: row.isEnabled || !row.hasKey }));
    } catch {
      setProvider(null);
    }
  }, []);

  const loadCodes = useCallback(async () => {
    if (!has) return;
    try { setCodes((await api.fetchCodes(companyId)).map(toCode)); } catch (err) { setError(err?.message || "Could not load HR codes."); }
  }, [companyId, has]);

  const loadMetrics = useCallback(async () => {
    if (!has) return;
    try {
      const rows = (await api.fetchMetrics(companyId, period.from, period.to)).map(toMetric);
      setMetrics(rows);
      const m = {};
      rows.forEach((r) => { m[r.metricCode] = r.value === null || r.value === undefined ? "" : String(Number(r.value)); });
      setManual(m);
    } catch (err) {
      setError(err?.message || "Could not load HR figures.");
    }
  }, [companyId, has, period.from, period.to]);

  useEffect(() => { loadStatus(); loadCodes(); }, [loadStatus, loadCodes]);
  useEffect(() => { loadProvider(); }, [loadProvider]);
  useEffect(() => { loadMetrics(); }, [loadMetrics]);

  // ---- connector -------------------------------------------------------------
  const active = connections.find((c) => c.status !== "DISCONNECTED") || null;

  const saveProvider = () =>
    guard("provider", async () => {
      await api.saveProvider(providerForm);
      setProviderForm((f) => ({ ...f, apiKey: "" }));
      await loadProvider();
      await loadStatus();
    }, "HR connector settings saved.");

  const connect = () =>
    guard("connect", async () => {
      const link = g(await api.createConnectLink(companyId, companyName || `Company ${companyId}`), "Link");
      const token = await api.openConnectWidget(link);
      if (!token) throw new Error("The connection was not completed.");
      setConnections((await api.activateConnection(companyId, token)).map(toConnection));
    }, "HR system connected.");

  const sync = () =>
    active && guard("sync", async () => {
      const r = await api.syncNow(active.id, period.from, period.to);
      setConnections((g(r, "Connections") || []).map(toConnection));
      const rows = (await api.fetchMetrics(companyId, period.from, period.to)).map(toMetric);
      setMetrics(rows);
      return feedReport(companyId, period.from, period.to, metricsToReportSections(rows));
    }, (fed) => `Figures pulled for ${period.from} → ${period.to}. ${fed || ""}`);

  const disconnectNow = () =>
    active && window.confirm(`Disconnect ${active.toolName}? Figures already pulled are kept.`) &&
    guard("disconnect", async () => setConnections((await api.disconnect(active.id)).map(toConnection)), "HR system disconnected.");

  // ---- own HR codes ------------------------------------------------------------
  const addCode = () => {
    if (!newCode.code.trim()) return setError("Enter the code.");
    return guard("codes", async () => {
      setCodes((await api.importCodes(companyId, [{ kind: newCode.kind, code: newCode.code.trim(), description: newCode.description.trim() }])).map(toCode));
      setNewCode((n) => ({ ...n, code: "", description: "" }));
    }, "Code added.");
  };

  /** Paste lines "kind,code,description" or "code,description" (kind = the picker's kind). */
  const importPasted = () => {
    const rows = pasteText.split(/\r?\n/).map((l) => l.split(/[;,\t]/).map((x) => x.trim())).filter((p) => p[0]);
    if (!rows.length) return setError("Paste one code per line.");
    const kinds = new Set(["CONTRACT_TYPE", "EMPLOYEE_CATEGORY", "ABSENCE_TYPE", "LEAVER_REASON", "GENDER", "OTHER"]);
    const payload = rows.map((p) =>
      kinds.has(p[0].toUpperCase()) ? { kind: p[0].toUpperCase(), code: p[1] || "", description: p[2] || "" } : { kind: newCode.kind, code: p[0], description: p[1] || "" },
    ).filter((r) => r.code);
    return guard("codes", async () => {
      setCodes((await api.importCodes(companyId, payload)).map(toCode));
      setPasteText("");
    }, `${payload.length} code(s) imported.`);
  };

  const setTarget = (code, target) =>
    guard(`map:${code.id}`, async () => setCodes((await api.mapCode(code.id, target)).map(toCode)), target ? `${code.code} → mapped.` : `${code.code} unmapped.`);

  const dropCode = (code) =>
    window.confirm(`Remove code ${code.code}?`) &&
    guard(`map:${code.id}`, async () => setCodes((await api.removeCode(code.id)).map(toCode)), "Code removed.");

  // ---- manual figures ----------------------------------------------------------------
  const setManualValue = (code, v) => setManual((m) => ({ ...m, [code]: v }));
  const saveManual = () =>
    guard("manual", async () => {
      const payload = HR_METRICS.map((d) => ({ code: d.code, value: manual[d.code] ?? "", unit: d.unit }));
      const rows = (await api.saveManualMetrics(companyId, period.from, period.to, payload)).map(toMetric);
      setMetrics(rows);
      return feedReport(companyId, period.from, period.to, metricsToReportSections(rows));
    }, (fed) => `Figures saved. ${fed || ""}`);

  const metricSource = useMemo(() => Object.fromEntries(metrics.map((m) => [m.metricCode, m])), [metrics]);
  const mappedCount = codes.filter((c) => c.target).length;

  return {
    hasCompany: has,
    period, setPeriod,
    error, notice, busy,
    // connector
    available, connections, active, provider, providerForm, setProviderForm, saveProvider, connect, sync, disconnectNow,
    // codes
    codes, mappedCount, newCode, setNewCode, addCode, pasteText, setPasteText, importPasted, setTarget, dropCode,
    // manual
    metrics, metricSource, manual, setManualValue, saveManual,
  };
}
